import React, { useState } from 'react';
import ProductImage1 from '../assets/product/product1.webp';
import Omega3BottleImage from '../assets/omega3/omega3-thumbnail.webp';
import ProductImage3 from '../assets/product/product3.webp';
import NinetyDays from '../assets/product/90days.png';
import OneMonth from '../assets/product/30days.png';
import ThreeMonth from '../assets/product/oneMonthNew.png';
import SixMonth from '../assets/product/threeMonthsNew.png';
import TryOnceProductImage from '../assets/product/tryonce.webp';
import GreenTickRound from '../assets/green-tick-round.png';
import { Icon } from './elements/icon';
import { useTranslation } from 'react-i18next';
import { isGermanLanguage } from '../utils/language';
import {
  MonthlyPlan,
  ThreeMonthPlan,
  StartNowButton,
  PerDay,
  Month,
  EveryThreeMonths,
  TryOnceMobile,
  Save,
  Subscription,
  Or,
  PauseAndCancel,
  SinglePurchase,
  ThreeMonthsCourse,
  BestValueMobile,
  LovedByNewCustomer,
  DailyServings,
  FreeGiftsMobile,
  Save10,
  CancelSkipEdit,
  TryOnceMobileTitle,
  ChooseYourPlan,
  BestToStart,
  AutoApplied,
  ChristmasDiscount,
  UnlockFreeGiftsWithSubscription,
  Gratis,
  Locked,
  TravelBag,
  MetalContainer,
  WellnessGuide,
  HabitTracker,
  FreeShippingEU,
  FreeGiftsWithSubscription,
  WelcomeGifts,
} from '../Constants';
import BagImg from '../assets/bag.webp';
import PackingImg from '../assets/packing.webp';
import GiftLockImg from '../assets/gift-pack.webp';
import WellnessGuideImg from '../assets/wellness-guide-cover.webp';
import HabitTrackerImg from '../assets/habit-tracker.webp';
import GiftImg from '../assets/Gift.webp';
import TickImgBlack from '../assets/black-tick.png';
import GuaranteeTick from '../assets/Guarantee.webp';
import CountdownTimer from './CountdownTimer';
import PurchaseCardDetails from './Customer/components/PurchaseCardDetails';

// Utility functions
const DURATION_ORDER = ['3 months', '6 months', '90 days'];

const getPriceByDuration = (duration, totals, multiplier = 1) => {
  const priceMap = {
    '6 months': totals.sixMonths,
    '3 months': totals.threeMonths,
    '90 days': totals.threeMonthsOneOff,
    '1 month': totals.oneMonth,
    '30 days': totals.oneMonth,
  };
  return (parseFloat(priceMap[duration] || 0) * multiplier).toFixed(2);
};

const getPerDayPrice = (price, duration) => {
  const days = duration === '6 months' || duration === '90 days' ? 90 : 30;
  return (price / days).toFixed(2);
};

// Reusable Components
const CustomCheckbox = ({ checked = false, ...props }) => (
  <button
    type="button"
    className={`t-w-5 t-h-5 t-rounded-full t-border-2 t-flex t-items-center t-justify-center t-transition-all t-duration-200 t-mt-[3px] ${
      checked ? 't-bg-white t-border-white t-p-[3px]' : 't-bg-transparent t-border-g300'
    }`}
    {...props}
  >
    {checked && <div className="t-w-full t-h-full t-bg-navy t-rounded-full" />}
  </button>
);

const DividerWithText = ({ text }) => (
  <div className="t-flex t-items-center">
    <div className="t-flex-grow t-border-t t-border-gray-400" />
    <span className="t-mx-3 t-text-gray-800 t-font-semibold t-tracking-wide">{text}</span>
    <div className="t-flex-grow t-border-t t-border-gray-400" />
  </div>
);

const ProductHeader = ({
  username,
  bannerImage,
  productImage,
  duration,
  t,
  showBannerImage = true,
  showOmega3Bottle = false,
}) => (
  <div className="t-flex t-flex-col t-items-start">
    <p className="t-m-0 t-font-serif t-font-normal t-text-navy t-text-2xl">{t(ChooseYourPlan)}</p>
    <div className="t-w-full t-flex t-flex-row t-items-end t-justify-between t-gap-2">
      {showBannerImage && (
        <img
          src={bannerImage}
          alt="Plan banner"
          className={`t-mix-blend-multiply ${['90 days', '30 days'].includes(duration) && 't-h-[73px]'} ${
            ['3 months', '6 months'].includes(duration) && 't-h-[100px]'
          }`}
        />
      )}
      <div className="t-flex t-items-end">
        {showOmega3Bottle && (
          <img
            src={Omega3BottleImage}
            alt={t('Omega3_Selection_ImageAlt')}
            loading="lazy"
            className="!t-h-20 !t-w-20 t-min-w-0 t-object-contain t-mr-[-20px] t-mb-[10px]"
          />
        )}
        <img
          src={productImage}
          alt="Product"
          className={`t-mix-blend-multiply ${
            ['6 months', '3 months', '1 month'].includes(duration)
              ? '!t-h-[155px] !t-w-[155px] t-object-contain'
              : '!t-h-[155px] !t-w-[155px] t-object-contain'
          } t-w-[40%]`}
        />
      </div>
    </div>
  </div>
);

const PlanBadge = ({ duration, t }) => {
  if (duration === '6 months') {
    return (
      <p className="!t-text-[10px] !t-m-0 !t-absolute !t-right-3 !-t-top-[10px] t-bg-gray-300 t-text-gray-700 t-px-4 t-z-10">
        {t(BestValueMobile)}
      </p>
    );
  }
  if (duration === '3 months') {
    return (
      <p className="!t-text-[10px] !t-m-0 !t-absolute !t-right-3 !-t-top-[10px] t-bg-butter t-text-navy t-font-semibold t-px-4 t-z-10">
        {t(LovedByNewCustomer)}
      </p>
    );
  }
  return null;
};

const getWelcomeGiftsValue = (duration) => (duration === '6 months' ? 51 : 26);

const PlanDetails = ({ isSubscription, duration, t, perDay }) => {
  if (isSubscription) {
    const welcomeGiftsValue = getWelcomeGiftsValue(duration);

    return (
      <div className="t-relative t-flex t-items-center t-justify-between t-p-2">
        <div className="t-flex t-flex-col t-gap-2 t-text-gray-600">
          <DetailItem
            icon={TickImgBlack}
            text={`${['6 months', '90 days'].includes(duration) ? '90' : '30'} ${t(DailyServings)}`}
          />
          <DetailItem icon={GuaranteeTick} text={t(PauseAndCancel)} />
          <DetailItem icon={GiftImg}>
            <span className="t-line-through">{welcomeGiftsValue}€</span> {t(FreeGiftsMobile)}
          </DetailItem>
        </div>
        <PerDayBadge price={perDay} t={t} />
      </div>
    );
  }

  return (
    <div className="t-relative t-flex t-items-center t-justify-between t-p-2">
      <div className="t-flex t-flex-col t-gap-2 t-text-gray-600">
        <DetailItem
          icon={TickImgBlack}
          text={`${duration === '90 days' ? '90' : '30'} ${t(DailyServings)}`}
        />
        <DetailItem icon={TickImgBlack} text={t(SinglePurchase)} />
      </div>
      <PerDayBadge price={perDay} t={t} />
    </div>
  );
};

const DetailItem = ({ icon, text, children }) => (
  <div className="t-flex t-items-center t-gap-2">
    <img src={icon} alt="" className="t-h-4 t-w-4 t-flex-shrink-0" />
    <p className="t-m-0 t-font-sans t-text-[13px] t-text-navy/80">{children || text}</p>
  </div>
);

const PerDayBadge = ({ price, t }) => (
  <div className="t-flex-shrink-0 t-flex t-flex-col t-items-center t-justify-center t-bg-butter t-text-navy t-rounded-lg t-px-3.5 t-py-2 t-text-center">
    <span className="!t-m-0 t-font-sans t-text-[18px] t-font-bold t-leading-none">{price}€</span>
    <span className="!t-m-0 t-font-sans t-text-[10px] t-font-medium t-leading-none t-mt-1 t-opacity-70">
      {t(PerDay)}
    </span>
  </div>
);

// Compact locked gift: small icon tile, way smaller than the unlocked version.
const LockedGift = ({ t }) => (
  <div className="t-flex t-flex-col t-items-center t-gap-1 t-w-[56px]">
    <div className="t-w-14 t-h-14 t-flex t-items-center t-justify-center t-bg-g100 t-rounded-lg">
      <img src={GiftLockImg} alt="" className="t-h-7 t-w-7 t-object-contain t-opacity-40" />
    </div>
    <span className="t-font-sans t-text-navy/40 t-text-[11px] t-font-medium">{t(Locked)}</span>
  </div>
);

// Unlocked gift, IM8-style: image on a soft tint (no grey box), struck price + label below.
const GiftCard = ({
  oldPrice,
  image,
  locked,
  label,
  description,
  t,
  tilePad = 't-p-2',
  imgMaxH = 't-max-h-[68px]',
  tileBg = 't-bg-teal-bg',
}) => {
  if (locked) return <LockedGift t={t} />;

  return (
    <div className="t-flex t-flex-col t-items-center t-gap-1 t-basis-[104px] t-shrink t-min-w-0 t-max-w-[104px]">
      <div
        className={`t-relative t-w-full t-h-[88px] t-flex t-items-center t-justify-center ${tileBg} t-rounded-xl ${tilePad}`}
      >
        <img src={image} alt={label} className={`${imgMaxH} t-max-w-full t-object-contain`} />
      </div>
      {!description && (
        <div className="t-flex t-min-h-[16px] t-items-center t-gap-1">
          {oldPrice && (
            <span className="t-line-through t-text-navy/50 t-font-sans t-text-[11px]">{oldPrice}</span>
          )}
          <span className="t-text-quiz-teal t-font-sans t-font-bold t-text-[11px]">{t(Gratis)}</span>
        </div>
      )}
      <span className="t-font-sans t-text-navy t-font-medium t-text-[11px] t-text-center t-leading-tight">
        {description || label}
      </span>
    </div>
  );
};

// Gifts inside the selected plan box. enabled (subscription) -> real gifts; !enabled -> locked.
// Longer plan (6 months / 90 days) gets the bag + container; shorter plan gets the container only.
const PlanGifts = ({ enabled, duration, t }) => {
  const { i18n } = useTranslation();
  const isGerman = isGermanLanguage(i18n.language);
  const hasBag = ['6 months', '90 days'].includes(duration);
  const hasContainer = ['6 months', '3 months', '30 days', '90 days'].includes(duration);
  // Physical habit tracker: unlocked welcome gift on the 3-month plan (internal '6 months');
  // also shown on the 3-month one-off ('90 days'), where it stays locked to upsell the Abo.
  const hasHabitTracker = ['6 months', '90 days'].includes(duration);

  if (!hasBag && !hasContainer && !hasHabitTracker) return null;

  return (
    <div className="t-flex t-flex-col t-gap-3 t-w-full t-px-2 t-pb-3 t-pt-1">
      <div className="t-flex t-items-center t-gap-2">
        <span className="t-text-[15px] t-leading-none" aria-hidden="true">
          🎁
        </span>
        <p className="!t-m-0 t-font-sans t-text-[13px] t-font-semibold t-text-navy">
          {enabled ? t(WelcomeGifts) : t(UnlockFreeGiftsWithSubscription)}
        </p>
      </div>
      <div className="t-flex t-flex-row t-gap-3 t-items-start t-justify-start">
        {hasBag && (
          <GiftCard
            oldPrice="19€"
            image={BagImg}
            locked={!enabled}
            label={t(TravelBag)}
            description={isGerman ? t('Product_GiftTravelBag') : undefined}
            t={t}
            tilePad="t-p-1"
            imgMaxH="t-max-h-[82px]"
          />
        )}
        {hasContainer && (
          <GiftCard
            oldPrice="11€"
            image={PackingImg}
            locked={!enabled}
            label={t(MetalContainer)}
            description={isGerman ? t('Product_GiftMetalContainer') : undefined}
            t={t}
          />
        )}
        {/* Digital wellness guide: no shipping, so it's offered on every abo plan. */}
        <GiftCard
          oldPrice="9€"
          image={WellnessGuideImg}
          locked={!enabled}
          label={t(WellnessGuide)}
          description={isGerman ? t('Product_GiftWellnessGuide') : undefined}
          t={t}
        />
        {/* Physical habit tracker: unlocked on the 3-month plan, locked on the 3-month one-off. */}
        {hasHabitTracker && (
          <GiftCard
            oldPrice="6€"
            image={HabitTrackerImg}
            locked={!enabled}
            label={t(HabitTracker)}
            description={isGerman ? t('Product_GiftHabitTracker') : undefined}
            t={t}
          />
        )}
      </div>
      {/* Free-shipping callout: subscription plans only — hidden on the one-off plans. */}
      {!['90 days', '30 days'].includes(duration) && (
        <div className="t-rounded-lg t-bg-green-500/10 t-px-3 t-py-2 t-text-center">
          <p className="!t-m-0 t-font-sans t-text-[13px] t-text-navy/80">
            + <span className="t-line-through t-opacity-50">6€</span>{' '}
            <span className="t-font-bold t-text-quiz-teal">{t(FreeShippingEU)}</span>
          </p>
        </div>
      )}
    </div>
  );
};

const SubscriptionCard = ({ item, isActive, totals, onSelectPlan, enabled, t }) => {
  const { i18n } = useTranslation();
  const isSubscription = ['6 months', '3 months'].includes(item.duration);
  const monthlyPrice = getPriceByDuration(item.duration, totals);
  const totalPrice =
    item.duration === '3 months' || item.duration === '30 days' || item.duration === '90 days'
      ? Math.round(monthlyPrice)
      : Math.round(monthlyPrice * 3);

  const strikethroughPrice =
    item.duration === '3 months'
      ? Math.round(parseFloat(totals.oneMonth || 0))
      : Math.round(parseFloat(totals.oneMonth || 0) * 3);
  const perDay = getPerDayPrice(totalPrice, item.duration);

  const getPlanTitle = () => {
    if (item.duration === '30 days') return t(TryOnceMobileTitle);
    if (item.duration === '90 days') return t(ThreeMonthsCourse);
    if (item.duration === '3 months') return t(MonthlyPlan);
    return t(ThreeMonthPlan);
  };

  return (
    <div
      className={`t-rounded-xl t-mb-3 t-overflow-hidden t-cursor-pointer t-transition-all t-duration-150 t-border-2 t-bg-white ${
        isActive ? 't-border-navy' : 't-border-g200'
      }`}
      onClick={() => onSelectPlan(item)}
    >
      <div
        className={`t-flex t-items-center t-justify-between t-px-3 t-py-3 ${
          isActive ? 't-bg-navy t-text-white' : 't-bg-g100 t-text-navy'
        }`}
      >
        <div className="t-flex t-items-start t-gap-2">
          <CustomCheckbox checked={isActive} />
          <div className="t-flex t-flex-col t-gap-0.5">
            <h3
              className={`!t-m-0 t-font-sans t-text-[15px] t-flex t-items-center t-gap-2 ${isActive ? 't-font-bold t-text-white' : 't-font-semibold t-text-navy'}`}
            >
              {getPlanTitle()}
              {item?.duration === '3 months' && (
                <span className="t-px-2 t-py-0.5 t-bg-quiz-teal t-text-white t-text-[10px] t-font-bold t-rounded">
                  {t(BestToStart)}
                </span>
              )}
            </h3>
            {(isSubscription || item.duration === '90 days') && (
              <span
                className={`t-font-sans t-text-[11px] t-font-semibold t-w-fit t-px-1.5 t-py-0.5 t-rounded ${isActive ? 't-bg-butter t-text-navy' : 't-bg-butter t-text-navy'}`}
              >
                {isGermanLanguage(i18n.language) ? (
                  t('Product_SavePercent', {
                    percent: item.duration === '3 months' ? 10 : item.duration === '90 days' ? 20 : 30,
                  })
                ) : (
                  <>{t(Save)} {item.duration === '3 months' ? '10%' : item.duration === '90 days' ? '20%' : '30%'}</>
                )}
              </span>
            )}
          </div>
        </div>
        <div className="t-flex t-flex-col t-items-end">
          <p
            className={`t-m-0 t-font-sans t-text-xl t-font-bold ${isActive ? 't-text-white' : 't-text-navy'}`}
          >
            {item?.duration !== '30 days' && (
              <span className="t-line-through t-mr-1 t-opacity-40 t-text-base">{strikethroughPrice}€</span>
            )}
            {totalPrice}€
          </p>
          <p
            className={`!t-m-0 t-font-sans t-text-[13px] ${isActive ? 't-text-white/70' : 't-text-navy/60'}`}
          >
            {item.duration === '90 days' ? Math.round(monthlyPrice / 3) : Math.round(monthlyPrice)}€{' '}
            {'/' + t(Month)}
          </p>
        </div>
      </div>
      {isActive && (
        <>
          <PlanDetails isSubscription={isSubscription} duration={item.duration} t={t} perDay={perDay} />
          <div className="t-border-t t-border-g200 t-mx-2" />
          <PlanGifts enabled={enabled} duration={item.duration} t={t} />
        </>
      )}
    </div>
  );
};

function ToggleSwitch({ enabled }) {
  return (
    <span
      aria-hidden="true"
      className={`
        t-flex t-items-center t-justify-center t-flex-shrink-0
        t-h-7 t-w-7 t-rounded-md t-p-0 t-border-2
        t-transition-colors
        ${enabled ? 't-bg-quaternary t-border-quaternary' : 't-bg-white t-border-g300'}
      `}
    >
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`t-h-4 t-w-4 t-transition-opacity t-duration-150 ${
          enabled ? 't-opacity-100' : 't-opacity-0'
        }`}
      >
        <path d="M4 10.5l4 4 8-9" />
      </svg>
    </span>
  );
}

// Main Component
const MobileSubscriptionCardV4 = ({
  username,
  subscribtion = {},
  subs = [],
  onSelectPlan,
  totals = {},
  paymentLink,
  canCheckoutWithoutPaymentLink = false,
  isCheckoutLoading = false,
  onSubmitData,
  error = {},
  showBtn = true,
  boxRef,
  focusArea,
  totalIngredients,
  ingredients = [],
  testimonialsSlot = null,
  proofTitleClassName,
  canShowStrikeThrough = true,
  productImageOverride = null,
  showBannerImage = true,
  ctaPriceTextOverride = null,
  ctaStrikeThroughPriceOverride = null,
  omega3AddOnPrice = 0,
  showOmega3Benefit = false,
  showOmega3Bottle = false,
  omega3BenefitKey,
}) => {
  const { t, i18n } = useTranslation();
  const { duration = '' } = subscribtion;
  const [enabled, setEnabled] = useState(true);

  // const sortedSubs = useMemo(
  //   () => subs.sort((a, b) => DURATION_ORDER.indexOf(a.duration) - DURATION_ORDER.indexOf(b.duration)),
  //   [subs]
  // );

  const bannerImages = {
    '6 months': SixMonth,
    '3 months': ThreeMonth,
    '1 month': OneMonth,
    '90 days': NinetyDays,
  };
  const productImages = {
    '6 months': ProductImage3,
    '3 months': ProductImage1,
    '30 days': TryOnceProductImage,
  };

  const bannerImage = bannerImages[duration] || OneMonth;
  const productImage = productImageOverride || productImages[duration] || TryOnceProductImage;

  const totalPrice = Math.round(getPriceByDuration(duration, totals, duration === '6 months' ? 3 : 1));
  const totalPriceForPerDay = totalPrice + Number(omega3AddOnPrice || 0);
  const perDayPrice = getPerDayPrice(totalPriceForPerDay, duration);
  const showStrikeThrough = canShowStrikeThrough && ['6 months', '3 months', '90 days'].includes(duration);
  const disabledBtn =
    isCheckoutLoading ||
    (!canCheckoutWithoutPaymentLink && !paymentLink) ||
    Object.values(error).some(Boolean);

  const getButtonPriceText = () => {
    if (duration === '30 days' || duration === '90 days') return `${totalPrice}€`;
    if (duration === '6 months') return `${totalPrice}€ ${t(EveryThreeMonths)}`;
    return `${totalPrice}€ /${t(Month)}`;
  };

  const updatedSubs = enabled ? subs?.slice(0, 2) : subs?.slice(2, 4);

  return (
    <div className="t-relative !t-w-full t-bg-surface t-flex t-flex-col t-pt-2 t-pb-4 md:t-pb-6 t-rounded-lg t-gap-2">
      <ProductHeader
        username={username}
        bannerImage={bannerImage}
        productImage={productImage}
        duration={duration}
        t={t}
        showBannerImage={showBannerImage}
        showOmega3Bottle={showOmega3Bottle}
      />

      <div className="t-flex t-flex-col t-justify-center !t-items-stretch">
        {updatedSubs?.map((item) => {
          const isActive = subscribtion?.subId === item?.id;

          return (
            <React.Fragment key={item.id}>
              {/* {item.duration === '3 months' && (
                <h1 className="t-text-base !t-font-[900]" style={{ fontFamily: 'Roboto' }}>
                  {t(Subscription)}
                </h1>
              )}
              {['30 days'].includes(item.duration) && (
                <>
                  <h1 className="t-text-base !t-font-[900]" style={{ fontFamily: 'Roboto' }}>
                    {t(TryOnceMobile)}
                  </h1>
                </>
              )} */}
              <SubscriptionCard
                item={item}
                isActive={isActive}
                totals={totals}
                onSelectPlan={onSelectPlan}
                enabled={enabled}
                t={t}
              />
            </React.Fragment>
          );
        })}
        <button
          type="button"
          role="checkbox"
          aria-checked={enabled}
          onClick={() => {
            onSelectPlan(
              enabled && subscribtion?.duration === '3 months'
                ? subs[2]
                : enabled && subscribtion?.duration === '6 months'
                  ? subs[3]
                  : subscribtion?.duration === '90 days'
                    ? subs[1]
                    : subs[0],
            );
            setEnabled(!enabled);
          }}
          className="t-flex t-items-center t-gap-3 t-mb-1 t-p-3 t-w-full t-text-left t-rounded-xl t-border-0 t-bg-g100 t-cursor-pointer"
        >
          <ToggleSwitch enabled={enabled} />
          <span className="t-block">
            <span className="t-block t-m-0 t-font-sans t-text-[14px] t-font-semibold t-text-navy">{t(Save10)}</span>
            <span className="t-block t-m-0 t-font-sans t-text-[13px] t-text-navy/60">{t(CancelSkipEdit)}</span>
          </span>
        </button>
      </div>

      {showBtn && (
        <button
          ref={boxRef}
          className={`t-w-full t-py-[15px] t-px-6 t-rounded-full t-bg-quaternary t-text-white t-font-sans t-text-[16px] t-font-bold ${isGermanLanguage(i18n.language) ? '' : 't-uppercase'} t-tracking-[0.5px] t-border-0 t-cursor-pointer hover:t-bg-[#2da898] disabled:t-opacity-30 disabled:t-cursor-not-allowed t-transition-colors`}
          onClick={() => onSubmitData(paymentLink)}
          disabled={disabledBtn}
        >
          {t(StartNowButton)} -{' '}
          {showStrikeThrough && (
            <span className="t-line-through t-opacity-60">
              {ctaStrikeThroughPriceOverride ??
                (['6 months', '90 days'].includes(duration)
                  ? Math.round(parseFloat(totals.oneMonth || 0) * 3)
                  : Math.round(parseFloat(totals.oneMonth || 0)))}
              €
            </span>
          )}{' '}
          <span className="t-font-bold">{ctaPriceTextOverride || getButtonPriceText()}</span>
        </button>
      )}

      <PurchaseCardDetails
        duration={duration}
        focusArea={focusArea}
        totalIngredients={totalIngredients}
        ingredients={ingredients}
        perDayPriceLabel={`${perDayPrice}€`}
        testimonialsSlot={testimonialsSlot}
        proofTitleClassName={proofTitleClassName}
        showOmega3Benefit={showOmega3Benefit}
        omega3BenefitKey={omega3BenefitKey}
        promotionSlot={enabled ? (
          <div className="t-flex t-w-full t-items-center t-gap-2 t-rounded-xl t-border t-border-green-500/30 t-bg-green-500/10 t-p-3">
            <Icon name="tealCheckCircle" size={18} color="#22c55e" />
            <p className="!t-m-0 t-font-sans t-text-[13px] t-text-navy">
              {isGermanLanguage(i18n.language)
                ? t('Product_SubscriptionIncluded')
                : <><span className="t-font-semibold t-text-green-500">{t(ChristmasDiscount)}</span>{' '}{t(AutoApplied)}</>}
            </p>
          </div>
        ) : null}
      />
    </div>
  );
};

export default MobileSubscriptionCardV4;
